import { useState, useEffect, useMemo, useRef } from 'react';
import Field from './Field';
import api from '../services/api';
import { listGroupChildren, isCarer } from '../services/children';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './Avatar';
import { createSuggestion } from '../services/suggestions';

/**
 * SuggestIdeaModal — compose and send a gift idea to another group member
 * ("would you be interested?"). Cloned from WishFormModal / GroupFormModal:
 * a conditionally-rendered component with fixed-overlay markup, per-field
 * useState, a `saving` flag, an inline `error`, and onClose/onSaved props.
 *
 * The recipient must be someone you share a group with, so the modal loads the
 * viewer's groups (each with `members`) and offers a group picker + a recipient
 * picker scoped to that group's members (minus yourself). It also loads wish
 * categories for the optional category picker. All three send entry points open
 * this one modal, varying only what's prefilled.
 *
 * Props:
 *  - prefill: optional starting values, any of:
 *      { recipientUserId, groupId, sourceWishId, title, description, url,
 *        imageUrl, priceMin, priceMax, categoryId }
 *  - onClose:  close without sending
 *  - onSaved:  called with the created suggestion after a successful send
 *  - notify:   the host page's flash helper, notify(type, text) (optional)
 */

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function SuggestIdeaModal({ prefill = {}, onClose, onSaved, notify }) {
    const { user } = useAuth();

    // Reference data.
    const [groups, setGroups] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    // Form fields.
    const [groupId, setGroupId] = useState(prefill.groupId ? String(prefill.groupId) : '');
    // Namespaced to match the option keys — a bare id would no longer select anything
    // now that children share the picker (PersonDetail and MembersTab both prefill this).
    const [recipientId, setRecipientId] = useState(
        prefill.recipientUserId ? `user:${prefill.recipientUserId}` : ''
    );
    const [title, setTitle] = useState(prefill.title || '');
    const [url, setUrl] = useState(prefill.url || '');
    const [imageUrl, setImageUrl] = useState(prefill.imageUrl || '');
    const [priceMin, setPriceMin] = useState(prefill.priceMin ?? '');
    const [priceMax, setPriceMax] = useState(prefill.priceMax ?? '');
    const [categoryId, setCategoryId] = useState(prefill.categoryId ? String(prefill.categoryId) : '');
    const [message, setMessage] = useState('');

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Product-image scrape from a pasted URL (same Open Graph fetch as WishFormModal).
    const [fetchingMeta, setFetchingMeta] = useState(false);
    const [metaNote, setMetaNote] = useState(null); // { warn, text }
    const lastFetchedUrl = useRef(prefill.url || '');

    // Load groups (with members) + categories once.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [groupsRes, catsRes] = await Promise.all([
                    api.get('/groups'),
                    api.get('/wish-categories'),
                ]);
                if (cancelled) return;
                setGroups(groupsRes.data);
                setCategories(catsRes.data);
                // If no group was prefilled, default to the first group that has
                // someone other than you to suggest to.
                if (!prefill.groupId) {
                    const firstUsable = (groupsRes.data || []).find((g) =>
                        (g.members || []).some((m) => m.id !== user?.id)
                    );
                    if (firstUsable) setGroupId(String(firstUsable.id));
                }
            } catch (err) {
                if (!cancelled) setError(errText(err, 'Could not load your groups.'));
            } finally {
                if (!cancelled) setLoadingRefs(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Load once on mount; prefill/user are stable for the modal's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedGroup = useMemo(
        () => groups.find((g) => String(g.id) === String(groupId)) || null,
        [groups, groupId]
    );

    // Children in the chosen group. Loaded separately because the groups payload
    // carries members but not children.
    const [groupChildren, setGroupChildren] = useState([]);
    useEffect(() => {
        let cancelled = false;
        if (!groupId) { setGroupChildren([]); return; }
        listGroupChildren(groupId)
            .then((rows) => { if (!cancelled) setGroupChildren(rows); })
            // A failure here should not block recommending to a person — the list just
            // stays empty and the children options don't appear.
            .catch(() => { if (!cancelled) setGroupChildren([]); });
        return () => { cancelled = true; };
    }, [groupId]);

    // Candidate recipients: people in the chosen group (minus yourself), plus any
    // children in it you do NOT look after.
    //
    // Carers are filtered out rather than shown-and-rejected: the API 422s a carer
    // recommending to their own child (they can add to the list directly), and offering
    // a choice that always fails is worse than not offering it.
    //
    // The value is namespaced ("user:5" / "child:3") because people and children are
    // separate id spaces — a bare id would merge user #5 with child #5, the same trap
    // OwnerKey exists to avoid on the wish side.
    const recipientOptions = useMemo(() => {
        if (!selectedGroup) return [];
        const people = (selectedGroup.members || [])
            .filter((m) => m.id !== user?.id)
            .map((m) => ({
                key: `user:${m.id}`, name: m.name, avatarUrl: m.avatar_url,
                color: m.avatar_color, seed: m.id, isChild: false,
            }));
        const kids = groupChildren
            .filter((c) => !isCarer(c, user?.id))
            .map((c) => ({
                key: `child:${c.id}`, name: c.name, avatarUrl: c.avatar_url,
                color: c.avatar_color, seed: c.id, isChild: true,
            }));
        return [...people, ...kids];
    }, [selectedGroup, user, groupChildren]);

    // If the recipient isn't a member of the chosen group, clear the selection so
    // we never submit a recipient/group mismatch (the API would 422).
    useEffect(() => {
        if (!recipientId) return;
        if (!recipientOptions.some((o) => o.key === recipientId)) {
            setRecipientId('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, recipientOptions]);

    const fetchMeta = async (raw) => {
        const u = (raw || '').trim();
        if (!/^https?:\/\/.+/i.test(u) || u === lastFetchedUrl.current) return;
        lastFetchedUrl.current = u;
        setFetchingMeta(true);
        setMetaNote(null);
        try {
            const res = await api.get('/url-metadata', { params: { url: u } });
            if (res.data.image) setImageUrl((prev) => prev || res.data.image);
            // Only fill the title if the user hasn't typed one.
            setTitle((prev) => (prev.trim() ? prev : res.data.title || prev));
            if (!res.data.image) {
                setMetaNote({ warn: true, text: "Couldn't find a product image for that link — paste one below." });
            }
        } catch (err) {
            setMetaNote({ warn: true, text: "Couldn't read that link — you can paste an image URL below." });
        } finally {
            setFetchingMeta(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setError('');

        if (!groupId) {
            setError('Pick a group you share with them.');
            return;
        }
        if (!recipientId) {
            setError('Pick who this idea is for.');
            return;
        }
        if (!title.trim()) {
            setError('Give the idea a title.');
            return;
        }

        // One namespaced value in, exactly one recipient field out — the API rejects
        // both together.
        const [kind, rawId] = recipientId.split(':');
        const payload = {
            ...(kind === 'child'
                ? { recipient_child_id: Number(rawId) }
                : { recipient_user_id: Number(rawId) }),
            group_id: Number(groupId),
            title: title.trim(),
            url: url.trim() || null,
            image_url: imageUrl.trim() || null,
            price_min: priceMin === '' ? null : priceMin,
            price_max: priceMax === '' ? null : priceMax,
            category_id: categoryId === '' ? null : Number(categoryId),
            message: message.trim() || null,
        };
        if (prefill.sourceWishId) payload.source_wish_id = prefill.sourceWishId;
        if (prefill.description) payload.description = prefill.description;

        setSaving(true);
        try {
            const created = await createSuggestion(payload);
            if (notify) notify('success', 'Recommendation sent.');
            onSaved?.(created);
        } catch (err) {
            setError(errText(err, 'Could not send the recommendation.'));
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full my-8">
                <h3 className="text-lg font-semibold mb-1">Recommend a gift</h3>
                <p className="text-sm text-gray-500 mb-4">
                    Send a gift idea to someone in one of your groups — they choose whether to add it to their list.
                </p>

                {loadingRefs ? (
                    <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <Field label="Group" required>
                            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputCls}>
                                <option value="">— pick a group —</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.icon ? `${g.icon} ` : ''}{g.name}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Recommend to" required>
                            {selectedGroup && recipientOptions.length === 0 ? (
                                <p className="text-sm text-amber-600">
                                    There's nobody here to recommend to yet — invite someone, or pick another group.
                                </p>
                            ) : (
                                <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                                    {recipientOptions.map((o) => {
                                        const checked = o.key === recipientId;
                                        return (
                                            <label
                                                key={o.key}
                                                className={`flex items-center gap-2 text-sm p-1 rounded cursor-pointer ${checked ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="recipient"
                                                    checked={checked}
                                                    onChange={() => setRecipientId(o.key)}
                                                />
                                                <Avatar name={o.name} url={o.avatarUrl} color={o.color} seed={o.seed} size={28} />
                                                <span className="text-gray-800">{o.name}</span>
                                                {o.isChild && (
                                                    <span className="text-xs text-gray-500">
                                                        · a carer will decide
                                                    </span>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </Field>

                        <Field label="Title" required>
                            <input
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className={inputCls}
                                placeholder="e.g., Wireless headphones"
                            />
                        </Field>

                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Product URL">
                                <input
                                    type="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    onBlur={(e) => fetchMeta(e.target.value)}
                                    onPaste={(e) => {
                                        const text = e.clipboardData.getData('text');
                                        setTimeout(() => fetchMeta(text), 0);
                                    }}
                                    className={inputCls}
                                    placeholder="https://…"
                                />
                                <p className={`text-xs mt-1 ${metaNote?.warn ? 'text-amber-600' : 'text-gray-400'}`}>
                                    {fetchingMeta
                                        ? 'Fetching image from link…'
                                        : metaNote?.text || 'Paste a product link to auto-fill the image.'}
                                </p>
                            </Field>
                            <Field label="Image URL">
                                <input
                                    type="url"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    className={inputCls}
                                    placeholder="https://…"
                                />
                                {imageUrl && (
                                    <img src={imageUrl} alt="" className="mt-2 h-16 w-16 object-cover rounded border border-gray-200" />
                                )}
                            </Field>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Min price">
                                <input type="number" min="0" step="0.01" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Max price">
                                <input type="number" min="0" step="0.01" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Category">
                                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls}>
                                    <option value="">— none —</option>
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.icon ? `${c.icon} ` : ''}{c.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <Field label="Message">
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className={inputCls}
                                rows={2}
                                placeholder="Would you be interested in this?"
                            />
                        </Field>

                        {error && <p className="text-sm text-red-600">{error}</p>}

                        <div className="flex gap-2 justify-end pt-2">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving || recipientOptions.length === 0}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {saving ? 'Sending…' : 'Send recommendation'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

